import type { CookieEntry, CookieCategory, StorageType } from './types.js';
import { matchCookieKnowledge, HEURISTICS } from './knowledge/cookies.js';
import { vendorForHost, vendorByKey } from './knowledge/vendors.js';

export interface RawObservation {
  name: string;
  domain: string;
  path?: string;
  type: StorageType;
  maxAgeSeconds: number | null;
  thirdParty: boolean;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None' | null;
  foundOnUrl?: string;
}

/**
 * Ordnet eine Beobachtung einer Kategorie zu.
 *
 * Reihenfolge der Entscheidung:
 *   1. bekannter Cookie-Name aus der Wissensbasis (hoechste Sicherheit)
 *   2. bekannter Anbieter anhand der setzenden Domain
 *   3. konservative Namensheuristik
 *   4. unclassified, damit ein Mensch entscheidet
 *
 * Wichtig: das Ergebnis ist ein Vorschlag, keine Rechtsauskunft. Eintraege
 * unter confidence 0.6 markiert das Dashboard als pruefungsbeduerftig.
 */
export function classify(obs: RawObservation): CookieEntry {
  const known = matchCookieKnowledge(obs.name, obs.thirdParty);
  const vendor = vendorForHost(obs.domain);

  let category: CookieCategory = 'unclassified';
  let purpose = '';
  let confidence = 0;
  let source: CookieEntry['classificationSource'] = 'unknown';

  if (known) {
    category = known.category;
    purpose = known.purpose;
    confidence = 0.95;
    source = 'knowledge_base';
  } else if (vendor) {
    category = vendor.category;
    purpose = `Wird von ${vendor.name} gesetzt. Zweck bitte pruefen und praezisieren.`;
    confidence = 0.7;
    source = 'knowledge_base';
  } else {
    for (const h of HEURISTICS) {
      if (h.test.test(obs.name)) {
        category = h.category;
        purpose = h.purpose;
        confidence = obs.thirdParty ? Math.max(0.4, h.confidence - 0.2) : h.confidence;
        source = 'heuristic';
        break;
      }
    }
  }

  // Ein Cookie einer Fremddomain als "notwendig" einzustufen, ist der haeufigste
  // Fehler in der Praxis. Deshalb: Vertrauen senken, ausser die Wissensbasis
  // kennt den Fall ausdruecklich.
  if (obs.thirdParty && category === 'necessary' && source === 'heuristic') {
    category = 'unclassified';
    purpose = '';
    confidence = 0;
    source = 'unknown';
  }

  if (category === 'unclassified' && !purpose) {
    purpose = 'Zweck noch nicht bestimmt. Bitte im Dashboard einordnen.';
  }

  // Anbieter zuerst ueber den in der Wissensbasis hinterlegten Schluessel.
  // Die setzende Domain hilft hier nicht: _ga steht auf der Kundendomain,
  // der Anbieter ist trotzdem Google. Ohne das bleibt die Pflichtangabe
  // "Anbieter" in der Cookie-Erklaerung leer.
  const knownVendor = (known?.vendorKey ? vendorByKey(known.vendorKey) : undefined) ?? vendor;

  return {
    name: obs.name,
    domain: obs.domain,
    path: obs.path,
    type: obs.type,
    category,
    purpose,
    vendor: knownVendor?.name,
    vendorPrivacyUrl: knownVendor?.privacyUrl,
    maxAgeSeconds: obs.maxAgeSeconds,
    thirdParty: obs.thirdParty,
    secure: obs.secure,
    httpOnly: obs.httpOnly,
    sameSite: obs.sameSite ?? null,
    foundOnUrl: obs.foundOnUrl,
    classificationSource: source,
    confidence,
  };
}

/** Menschlich lesbare Laufzeit fuer die Cookie-Erklaerung. */
export function formatLifetime(maxAgeSeconds: number | null, locale = 'de'): string {
  if (maxAgeSeconds === null) return locale === 'de' ? 'Sitzung' : 'Session';
  const days = Math.round(maxAgeSeconds / 86400);
  if (days < 1) {
    const hours = Math.max(1, Math.round(maxAgeSeconds / 3600));
    return locale === 'de' ? `${hours} Stunden` : `${hours} hours`;
  }
  if (days < 60) return locale === 'de' ? `${days} Tage` : `${days} days`;
  const months = Math.round(days / 30);
  if (months < 24) return locale === 'de' ? `${months} Monate` : `${months} months`;
  const years = Math.round(days / 365);
  return locale === 'de' ? `${years} Jahre` : `${years} years`;
}

/** Zaehlt Eintraege je Kategorie, fuer Dashboard-Kacheln. */
export function countByCategory(entries: CookieEntry[]): Record<CookieCategory, number> {
  const counts: Record<CookieCategory, number> = {
    necessary: 0,
    preferences: 0,
    statistics: 0,
    marketing: 0,
    unclassified: 0,
  };
  for (const e of entries) counts[e.category] += 1;
  return counts;
}
